import { useState, useEffect } from 'react';
import { Button } from '@habitech/call-to-action';
import { TextField } from '@habitech/keychain-atoms-inputs';
import Title from 'src/components/common/Title';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectUserProfile, selectSuggestedUserId } from 'src/redux/shopping_cart/shopping_cart.selectors';
import { updateUserProfile } from 'src/redux/shopping_cart/shopping_cart.slice';
import { selectUser } from 'src/redux/user/userSlice';
import { updateProfile } from 'src/apis/updateProfileApi';

const PHONE_LENGTH = 10;
const ONLY_NUMBERS_REGEX = /^\d*$/;
const ERROR_ONLY_NUMBERS = 'Solo se permiten números';
const ERROR_LENGTH = 'Debe tener 10 dígitos';

/**
 * Modal para editar el perfil del usuario.
 * Actualmente: teléfono de contacto y teléfono WhatsApp.
 * Extensible a más campos del perfil en el futuro.
 */
interface UserProfileEditModalProps {
    onClose: () => void;
}

const validatePhone = (value: string): string | null => {
    if (value === '') return null;
    if (!ONLY_NUMBERS_REGEX.test(value)) return ERROR_ONLY_NUMBERS;
    if (value.length !== PHONE_LENGTH) return ERROR_LENGTH;
    return null;
};

const UserProfileEditModal = ({ onClose }: UserProfileEditModalProps) => {
    const dispatch = useAppDispatch();
    const userProfile = useAppSelector(selectUserProfile);
    const userId = useAppSelector(selectSuggestedUserId);
    const { token } = useAppSelector(selectUser);
    const [phoneContact, setPhoneContact] = useState(userProfile?.phone_contact ?? '');
    const [phoneWhatsapp, setPhoneWhatsapp] = useState(userProfile?.phone_whatsapp ?? '');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ phone_contact: string | null; phone_whatsapp: string | null }>({
        phone_contact: null,
        phone_whatsapp: null,
    });

    useEffect(() => {
        setPhoneContact(userProfile?.phone_contact ?? '');
        setPhoneWhatsapp(userProfile?.phone_whatsapp ?? '');
    }, [userProfile?.phone_contact, userProfile?.phone_whatsapp]);

    const handleChange = (name: 'phone_contact' | 'phone_whatsapp', value: string) => {
        const rawValue = value ?? '';
        const numericValue = rawValue.replace(/\D/g, '').slice(0, PHONE_LENGTH);
        const error = validatePhone(rawValue);
        if (name === 'phone_contact') {
            setPhoneContact(numericValue);
            setErrors((prev) => ({ ...prev, phone_contact: error }));
        } else {
            setPhoneWhatsapp(numericValue);
            setErrors((prev) => ({ ...prev, phone_whatsapp: error }));
        }
    };

    const isValid =
        phoneContact.length === PHONE_LENGTH &&
        phoneWhatsapp.length === PHONE_LENGTH &&
        !errors.phone_contact &&
        !errors.phone_whatsapp;

    const handleSaveProfile = async () => {
        if (!token) {
            setSaveError('No hay sesión activa');
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const payload = {
                phone_contact: phoneContact,
                phone_whatsapp: phoneWhatsapp,
                update_for_customer: true,
                ...(userId ? { user_id_for_update: userId } : {}),
            };
            const res = await updateProfile(payload, token);
            if (res?.success) {
                dispatch(updateUserProfile({ phone_contact: phoneContact, phone_whatsapp: phoneWhatsapp }));
                onClose();
            } else {
                setSaveError(res?.message ?? 'Error al guardar el perfil');
            }
        } catch {
            setSaveError('Error de conexión al guardar el perfil');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const contactError = validatePhone(phoneContact);
        const whatsappError = validatePhone(phoneWhatsapp);
        setErrors({ phone_contact: contactError, phone_whatsapp: whatsappError });
        if (contactError || whatsappError) return;
        handleSaveProfile();
    };

    return (
        <div style={{ paddingTop: 24 }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title>Editar perfil</Title>
            </div>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                    <TextField
                        label="Teléfono de contacto"
                        originType="pcom"
                        name="phone_contact"
                        dataId="modal-phone-contact"
                        placeHolder="Teléfono de contacto"
                        value={phoneContact}
                        onChange={(_, value) => handleChange('phone_contact', value ?? '')}
                        error={!!errors.phone_contact}
                        errorMessage={errors.phone_contact ?? ''}
                    />
                </div>
                {saveError && (
                    <div style={{ marginBottom: 16, color: 'var(--color-error, #d32f2f)', fontSize: 14 }}>
                        {saveError}
                    </div>
                )}
                <div style={{ marginBottom: 16 }}>
                    <TextField
                        label="Teléfono WhatsApp"
                        originType="pcom"
                        name="phone_whatsapp"
                        dataId="modal-phone-whatsapp"
                        placeHolder="Teléfono WhatsApp"
                        value={phoneWhatsapp}
                        onChange={(_, value) => handleChange('phone_whatsapp', value ?? '')}
                        error={!!errors.phone_whatsapp}
                        errorMessage={errors.phone_whatsapp ?? ''}
                    />
                </div>
                <Button
                    type="submit"
                    originType="pcom"
                    variant="primary"
                    size="large"
                    dataId="button-send-modal"
                    disabled={!isValid || saving}
                >
                    {saving ? 'Guardando...' : 'Guardar'}
                </Button>
            </form>
        </div>
    );
};

export default UserProfileEditModal;
